/**
 * The ways into the rail when it is closed.
 *
 * On the desk, the 48px tab that names the page the agent follows. On the
 * phone, the empty corner opposite the menu button: no bar, nothing added to
 * the page. Both say when the agent is working (a thin turning ring) and grow
 * only when it is waiting on you, saying why.
 */
import type { MouseEvent } from "react";
import { MessageSquare } from "lucide-react";
import AgentMark from "@/app/routes/dashboard/components/icons/AgentMark";

export type RailStatus = {
  working: boolean;
  /** What the agent is waiting on you for ("1 question"), or null. */
  waiting: string | null;
};

function WorkingRing({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden>
      <circle cx="16" cy="16" r="14.5" stroke="var(--co-line2)" strokeWidth="1.5" />
      <circle
        cx="16"
        cy="16"
        r="14.5"
        stroke="var(--co-accent)"
        strokeWidth="1.5"
        strokeDasharray="22 70"
        strokeLinecap="square"
      />
    </svg>
  );
}

export function DeskTab({
  label,
  followLabel,
  open,
  status,
  onOpen,
  comments,
}: {
  label: string;
  followLabel: string;
  open: boolean;
  status: RailStatus;
  onOpen: () => void;
  /** A post page's comments: below a hairline, Comments with its count. */
  comments?: { count: number; onOpen: () => void } | null;
}) {
  const state = status.waiting
    ? ` Waiting on you: ${status.waiting}.`
    : status.working
      ? " Working."
      : "";
  const chat = (
    <button
      id="co-agent-tab"
      type="button"
      className="co-agent-tab"
      onClick={onOpen}
      aria-label={`Open Creo. Following ${followLabel}.${state}`}
      aria-expanded={open}
      aria-controls="co-agent-panel"
    >
      <AgentMark className="co-agent-tab-mark" aria-hidden />
      <span className="co-agent-tab-label">{label}</span>
      {status.waiting ? (
        <span className="co-agent-tab-waiting" aria-hidden>
          <span>{status.waiting}</span>
          <i />
        </span>
      ) : status.working ? (
        <WorkingRing className="co-agent-tab-ring" />
      ) : null}
    </button>
  );
  if (!comments) return chat;
  // Collapsed on a post page (DESIGN.md "Comments sit beside the chat"): the
  // chat, a hairline, then Comments with its count, in Barlow Condensed.
  return (
    <div className="co-agent-tab-stack">
      {chat}
      <span className="co-agent-tab-hairline" aria-hidden="true" />
      <button
        type="button"
        className="co-agent-tab co-agent-tab--comments"
        onClick={comments.onOpen}
        aria-label={`Open comments, ${comments.count} open`}
        aria-expanded={open}
        aria-controls="co-agent-panel"
      >
        <MessageSquare className="co-agent-tab-icon" strokeWidth={1.75} aria-hidden="true" />
        <span className="co-agent-tab-label">Comments</span>
        {comments.count > 0 ? (
          <span className="co-agent-tab-count" aria-hidden="true">{comments.count}</span>
        ) : null}
      </button>
    </div>
  );
}

/**
 * The strip where Creo cannot be used (Workshop, client view, no brand yet).
 * It keeps the closed strip's width so the desk never changes size between
 * pages; it is tinted and does nothing. aria-disabled, not disabled, so the
 * reason still shows on hover and is read out.
 */
export function DisabledDeskTab({ reason }: { reason: string }) {
  return (
    <button
      id="co-agent-tab"
      type="button"
      className="co-agent-tab"
      aria-disabled="true"
      aria-label={`Creo is unavailable. ${reason}`}
      title={reason}
    >
      <AgentMark className="co-agent-tab-mark" aria-hidden />
      <span className="co-agent-tab-label">Creo</span>
    </button>
  );
}

/** The phone corner for the same states: in place, tinted, inert. */
export function DisabledPhoneCorner({ reason, inLane = false }: { reason: string; inLane?: boolean }) {
  return (
    <button
      id="m7r-corner"
      type="button"
      className="m7r-corner"
      data-lane={inLane || undefined}
      aria-disabled="true"
      aria-label={`Creo is unavailable. ${reason}`}
      title={reason}
    >
      <span className="m7r-corner-mark">
        <AgentMark aria-hidden />
      </span>
    </button>
  );
}

export function PhoneCorner({
  open,
  status,
  onToggle,
  inLane = false,
}: {
  open: boolean;
  status: RailStatus;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Drawn as a button in a page's own top lane rather than in the corner. */
  inLane?: boolean;
}) {
  const state = status.waiting
    ? `, waiting on you: ${status.waiting}`
    : status.working
      ? ", working"
      : "";
  return (
    <button
      id="m7r-corner"
      type="button"
      className="m7r-corner"
      data-open={open || undefined}
      data-waiting={status.waiting ? true : undefined}
      data-lane={inLane || undefined}
      aria-label={`${open ? "Close" : "Open"} Creo${state}`}
      aria-expanded={open}
      aria-controls="co-agent-panel"
      onClick={onToggle}
    >
      <span className="m7r-corner-mark">
        <AgentMark aria-hidden />
        {status.working && !status.waiting ? <WorkingRing className="m7r-corner-ring" /> : null}
      </span>
      <span className="m7r-corner-label" aria-hidden>
        <span>
          {status.waiting}
          {status.waiting ? <i /> : null}
        </span>
      </span>
    </button>
  );
}
